import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.flat-map.js";
import "core-js/modules/es.iterator.map.js";
/**
 * Import core Vue APIs:
 * - defineComponent: used to declare a Vue component with type inference
 * - h: Vue's render function helper for creating VNodes manually
 * - PropType / VNode: TypeScript helper types
 */
import { sanitizeHTML } from '@common/utils/DOM';
import { defineComponent, h } from 'vue';
/**
 * Type guard that narrows a generic PatternMatch
 * into a TooltipMatch at runtime.
 *
 * This is necessary because all parsed matches
 * share a common base interface.
 */
function isTooltipMatch(data) {
  return typeof data.label === 'string' && typeof data.tooltip === 'string';
}
/* ─────────────────────────────────────────────────────────────────────────────
 * PATTERN DEFINITIONS
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * All supported inline text patterns.
 *
 * Patterns are:
 * - matched independently
 * - collected together
 * - sorted by position
 */
const patterns = [{
  type: 'tooltip',
  /**
   * Tooltip syntax:
   *
   *   (Label Text)["Tooltip text" optionalUrl]
   *
   * Capture groups:
   * 1 → label
   * 2 → tooltip text
   * 3 → optional URL
   */
  regex: /\(([^)]*)\)\["([^"]*)"(?:\s([^\]]*))?\]/g,
  /**
   * Convert the RegExp match array into
   * structured TooltipMatch data.
   */
  parse: ([, label, tooltip, url]) => ({
    label,
    tooltip,
    url
  })
}];
/* ─────────────────────────────────────────────────────────────────────────────
 * HELPERS
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * Creates an <a> VNode with tooltip behavior.
 *
 * This function encapsulates:
 * - URL handling
 * - hash-link prevention
 * - tooltip directive wiring
 */
function buildTooltipAnchor(label, tooltip, url) {
  /**
   * Default to "#" if no URL is provided.
   */
  const resolvedUrl = url ?? '#';
  /**
   * Detect in-page or placeholder links.
   */
  const isHash = resolvedUrl.startsWith('#');
  return h('a',
  /**
   * VNode props definition
   */
  {
    /**
     * Standard HTML attributes
     */
    attrs: {
      href: resolvedUrl,
      /**
       * Only open in new tab if this
       * is NOT a hash anchor.
       */
      ...(!isHash && {
        target: '_blank',
        rel: 'noopener noreferrer'
      })
    },
    /**
     * Prevent default click behavior
     * for hash-only links
     */
    ...(isHash && {
      on: {
        click: e => {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    }),
    /**
     * Attach tooltip directive
     * (assumes one is registered globally)
     */
    directives: [{
      name: 'tooltip',
      value: tooltip
    }]
  },
  /**
   * Anchor contents
   */
  [label]);
}
/* ─────────────────────────────────────────────────────────────────────────────
 * TEXT PARSER
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * Parses raw text into a mixture of:
 * - plain strings
 * - Vue VNodes
 *
 * This is where regex matching, ordering,
 * and replacement happens.
 */
function textToVnodes(text, patterns) {
  const allMatches = [];
  /**
   * Run ALL patterns against the text.
   */
  for (const pattern of patterns) {
    /**
     * Clone regex to avoid shared state
     * due to the global 'g' flag.
     */
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
    /**
     * Collect all matches for this pattern.
     */
    for (const match of text.matchAll(regex)) {
      allMatches.push({
        type: pattern.type,
        data: pattern.parse(Array.from(match)),
        index: match.index,
        end: match.index + match[0].length
      });
    }
  }
  /**
   * Sort matches by appearance in the string.
   */
  allMatches.sort((a, b) => a.index - b.index);
  const vnodes = [];
  let lastIndex = 0;
  /**
   * Walk matches in order and rebuild output.
   */
  for (const match of allMatches) {
    /**
     * Skip overlapping matches.
     */
    if (match.index < lastIndex) continue;
    /**
     * Push untouched text before match.
     */
    if (match.index > lastIndex) {
      vnodes.push(text.slice(lastIndex, match.index));
    }
    /**
     * Dispatch behavior based on pattern type.
     */
    if (isTooltipMatch(match.data)) {
      const {
        label,
        tooltip,
        url
      } = match.data;
      vnodes.push(buildTooltipAnchor(label, tooltip, url));
    }
    /**
     * Future patterns would go here.
     */
    lastIndex = match.end;
  }
  /**
   * Append any trailing text after last match.
   */
  if (lastIndex < text.length) {
    vnodes.push(text.slice(lastIndex));
  }
  return vnodes;
}
/* ─────────────────────────────────────────────────────────────────────────────
 * DOM → VNODE CONVERTER
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * Converts a DOM NodeList into Vue VNodes,
 * while preserving:
 * - element tags
 * - attributes
 * - nested children
 *
 * Text nodes are passed through the pattern parser.
 */
function domToVnodes(nodes, patterns) {
  return Array.from(nodes).flatMap(node => {
    /**
     * TEXT NODE:
     * Parse text contents for inline patterns.
     */
    if (node.nodeType === Node.TEXT_NODE) {
      return textToVnodes(node.textContent ?? '', patterns);
    }
    /**
     * ELEMENT NODE:
     * Recreate element with attributes and children.
     */
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node;
      /**
       * Convert NamedNodeMap → plain object.
       */
      const attrs = Object.fromEntries(Array.from(el.attributes).map(a => [a.name, a.value]));
      return [h(el.tagName.toLowerCase(), {
        attrs
      }, domToVnodes(el.childNodes, patterns))];
    }
    /**
     * Ignore comments and unsupported nodes.
     */
    return [];
  });
}
/* ─────────────────────────────────────────────────────────────────────────────
 * COMPONENT
 * ─────────────────────────────────────────────────────────────────────────────
 */
export default defineComponent({
  name: 'DisplayText',
  /**
   * Raw HTML/text input.
   *
   * This allows consumers to pass formatted
   * content instead of plain strings.
   */
  props: {
    text: {
      type: String,
      default: ''
    },
    fallback: {
      type: String,
      default: '--'
    },
    useFallback: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    return () => {
      /**
       * Parse the provided text into
       * a temporary HTML document.
       */
      let val = sanitizeHTML(props.text ?? '');
      // Handle empty
      if (!hasText(val) && props.useFallback) {
        val = props.fallback;
      }
      const doc = new DOMParser().parseFromString(val, 'text/html');
      /**
       * Convert DOM → VNodes with pattern replacement.
       */
      const vnodes = domToVnodes(doc.body.childNodes, patterns);
      /**
       * Render using display: contents
       * so this component does not affect layout.
       */
      return h('span', {
        style: 'display:contents'
      }, vnodes);
    };
  }
});
function hasText(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  // .textContent retrieves all text within elements, ignoring tags
  // .trim() removes whitespace like spaces and newlines
  return doc.body.textContent.trim().length > 0;
}